// Tracking → Overview tab.
//
// One API call (/admin/tracking/overview) backs every widget below.
// Lookback window is a URL-driven dropdown — pass-through via the same
// query param the API expects.
//
// Widgets:
//   - 6 KPI tiles (in-flight, delivered, exceptions, transit perfs, coverage)
//   - Status distribution donut (custom SVG — no chart lib dependency)
//   - Daily volume stacked bar (custom SVG)
//   - Top-5 fastest / slowest destination states tables

import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  Loader2, Plane, PackageCheck, AlertTriangle, Timer, BarChart2, Radar,
} from 'lucide-react'
import { getTrackingOverview } from '../../lib/api'

const CANONICAL_LABEL = {
  picked_up:                'Picked up',
  in_transit:               'In transit',
  at_hub:                   'At facility',
  out_for_delivery:         'Out for delivery',
  attempted:                'Attempted',
  delivered:                'Delivered',
  exception:                'Exception',
  // Derived bucket — AP recognises the consignment but the parcel
  // has handed off to a destination-country postal carrier, so AP
  // can't surface in-transit events. Distinct from 'unknown' (which
  // means we have events that didn't fit the vocab).
  international_in_transit: 'International (in transit)',
  // Terminal — the sender cancelled before the parcel entered the network.
  // Excluded from the tracking queries, so this only renders for a row that
  // was already on screen when the status flipped.
  cancelled:                'Cancelled',
  unknown:                  'Unknown',
}

const CANONICAL_COLOR = {
  picked_up:                '#94a3b8',
  in_transit:               '#2563eb',
  at_hub:                   '#7c3aed',
  out_for_delivery:         '#f59e0b',
  attempted:                '#d97706',
  delivered:                '#10b981',
  exception:                '#dc2626',
  international_in_transit: '#0891b2',  // cyan — distinct from the blue in_transit
  cancelled:                '#64748b',  // neutral slate — not a failure, just never shipped
  unknown:                  '#cbd5e1',
}

const CARRIER_COLOR = [
  '#2563eb', '#7c3aed', '#10b981', '#f59e0b', '#dc2626',
  '#0891b2', '#db2777', '#65a30d', '#475569',
]

function formatDays(v) {
  if (v == null) return '—'
  return `${v.toFixed(1)}d`
}

function formatNum(v) {
  if (v == null) return '—'
  return new Intl.NumberFormat('en-AU').format(v)
}

// ── KPI tile ────────────────────────────────────────────────────────────────

function KpiTile({ icon: Icon, label, value, sub, accent = 'text-slate-700' }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-slate-400" />
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${accent}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  )
}

// Two-up variant of KpiTile — left half = Standard, right half = Express.
// Used for the transit-time KPIs so the eyeballs see both service tiers
// at a glance (a single combined number hides the Express premium).
function SplitKpiTile({ icon: Icon, label, leftValue, rightValue, leftCount, rightCount, accent = 'text-slate-700' }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-slate-400" />
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className="grid grid-cols-2 divide-x divide-slate-100">
        <div className="pr-2">
          <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">Std</p>
          <p className={`text-xl font-bold ${accent}`}>{leftValue}</p>
          {leftCount != null && <p className="text-[10px] text-slate-400 mt-0.5">n={formatNum(leftCount)}</p>}
        </div>
        <div className="pl-2">
          <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">Exp</p>
          <p className={`text-xl font-bold ${accent}`}>{rightValue}</p>
          {rightCount != null && <p className="text-[10px] text-slate-400 mt-0.5">n={formatNum(rightCount)}</p>}
        </div>
      </div>
    </div>
  )
}

// ── Status donut (pure SVG, no chart lib) ───────────────────────────────────

function StatusDonut({ rows }) {
  const total = rows.reduce((s, r) => s + r.count, 0)
  if (!total) {
    return (
      <div className="card p-6 text-center text-slate-400 text-sm">
        No statuses to plot yet.
      </div>
    )
  }
  // Arc geometry
  const size = 180
  const r = 75
  const cx = size / 2
  const cy = size / 2
  let acc = 0
  const arcs = rows.map(row => {
    const frac  = row.count / total
    const a0    = acc * Math.PI * 2 - Math.PI / 2
    const a1    = (acc + frac) * Math.PI * 2 - Math.PI / 2
    acc        += frac
    const large = frac > 0.5 ? 1 : 0
    const x0    = cx + r * Math.cos(a0)
    const y0    = cy + r * Math.sin(a0)
    const x1    = cx + r * Math.cos(a1)
    const y1    = cy + r * Math.sin(a1)
    return {
      d: `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`,
      color: CANONICAL_COLOR[row.canonical_type] || '#cbd5e1',
      pct: Math.round(frac * 100),
      ...row,
    }
  })
  return (
    <div className="card p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">Status distribution</h3>
      <div className="flex flex-col sm:flex-row items-center gap-5">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0">
          {arcs.map((a, i) => <path key={i} d={a.d} fill={a.color} />)}
          {/* Donut hole */}
          <circle cx={cx} cy={cy} r={r * 0.6} fill="#ffffff" />
          <text x={cx} y={cy - 2} textAnchor="middle" className="fill-slate-700" fontSize="20" fontWeight="700">
            {formatNum(total)}
          </text>
          <text x={cx} y={cy + 14} textAnchor="middle" className="fill-slate-400" fontSize="9">
            orders
          </text>
        </svg>
        <ul className="flex-1 w-full space-y-1.5">
          {arcs.map((a, i) => (
            <li key={i} className="flex items-center gap-2 text-xs">
              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: a.color }} />
              <span className="flex-1 text-slate-700">{CANONICAL_LABEL[a.canonical_type] || a.canonical_type}</span>
              <span className="text-slate-500 tabular-nums">{formatNum(a.count)}</span>
              <span className="text-slate-400 tabular-nums w-9 text-right">{a.pct}%</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// ── Daily volume stacked bar ────────────────────────────────────────────────

function DailyVolumeChart({ rows }) {
  // Group rows by date → per-carrier counts. Pivot for stacking.
  const carriers = useMemo(() => Array.from(new Set(rows.map(r => r.carrier))).sort(), [rows])
  const carrierColor = useMemo(() => Object.fromEntries(carriers.map((c, i) => [c, CARRIER_COLOR[i % CARRIER_COLOR.length]])), [carriers])
  const byDate = useMemo(() => {
    const m = new Map()
    for (const r of rows) {
      if (!m.has(r.date)) m.set(r.date, {})
      m.get(r.date)[r.carrier] = (m.get(r.date)[r.carrier] || 0) + r.shipped
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [rows])

  if (!byDate.length) {
    return (
      <div className="card p-6 text-center text-slate-400 text-sm">
        No shipments in this window yet.
      </div>
    )
  }

  const maxTotal = Math.max(1, ...byDate.map(([, m]) => carriers.reduce((s, c) => s + (m[c] || 0), 0)))
  const w = 760, h = 220, pad = { top: 10, right: 16, bottom: 30, left: 36 }
  const innerW = w - pad.left - pad.right
  const innerH = h - pad.top - pad.bottom
  const barW = Math.max(2, Math.min(28, (innerW / byDate.length) - 2))

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">Daily volume by carrier</h3>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
          {carriers.map(c => (
            <span key={c} className="flex items-center gap-1.5 text-slate-600">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: carrierColor[c] }} />
              {c}
            </span>
          ))}
        </div>
      </div>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} className="block">
        {/* y-axis grid */}
        {[0, 0.5, 1].map((f, i) => {
          const y = pad.top + (1 - f) * innerH
          return (
            <g key={i}>
              <line x1={pad.left} x2={pad.left + innerW} y1={y} y2={y} stroke="#e2e8f0" />
              <text x={pad.left - 6} y={y + 4} textAnchor="end" className="fill-slate-400" fontSize="10">
                {Math.round(f * maxTotal)}
              </text>
            </g>
          )
        })}
        {/* Bars */}
        {byDate.map(([date, m], i) => {
          const x = pad.left + i * (innerW / byDate.length)
          let yCursor = pad.top + innerH
          return (
            <g key={date}>
              {carriers.map(c => {
                const n = m[c] || 0
                if (!n) return null
                const segH = (n / maxTotal) * innerH
                yCursor -= segH
                return <rect key={c} x={x + 1} y={yCursor} width={barW} height={segH} fill={carrierColor[c]} />
              })}
            </g>
          )
        })}
        {/* x-axis date ticks: every ~7th date */}
        {byDate.map(([date], i) => {
          if (i % Math.max(1, Math.ceil(byDate.length / 8)) !== 0) return null
          const x = pad.left + i * (innerW / byDate.length) + barW / 2
          return (
            <text key={date} x={x} y={h - 8} textAnchor="middle" className="fill-slate-400" fontSize="9">
              {date.slice(5)}
            </text>
          )
        })}
      </svg>
    </div>
  )
}

// ── Destination tables ──────────────────────────────────────────────────────

// Per-service-class state performance table. Fastest state at the top,
// slowest at the bottom — same shape on both sides (Std and Exp). Each
// row is colour-coded by relative speed within its OWN table: the
// fastest gets emerald, the slowest gets red, the middle band stays
// neutral. That keeps the eye drawn to outliers without us needing to
// pick a global threshold (express to WA is "slow" by Express standards
// but "fast" by Std standards).
function StatePerformanceTable({ title, subtitle, rows }) {
  const empty = !rows || rows.length === 0
  // Top quartile = emerald (fast), bottom quartile = red (slow).
  const accentForIndex = (i, total) => {
    if (total < 4) return 'text-slate-700'
    if (i === 0)              return 'text-emerald-600'
    if (i === total - 1)      return 'text-red-600'
    if (i < Math.ceil(total / 4))         return 'text-emerald-700'
    if (i >= total - Math.ceil(total / 4)) return 'text-red-700'
    return 'text-slate-700'
  }
  return (
    <div className="card p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {empty ? (
        <p className="text-xs text-slate-400 text-center py-6">
          Not enough delivered shipments in this class to rank yet (need ≥ 3 per state).
        </p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr>
              <th className="text-left font-medium py-1">State</th>
              <th className="text-right font-medium py-1">Median transit</th>
              <th className="text-right font-medium py-1">Delivered</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.state} className="border-t border-slate-100">
                <td className="py-1.5 font-medium text-slate-700">{r.state}</td>
                <td className={`py-1.5 text-right tabular-nums font-medium ${accentForIndex(i, rows.length)}`}>
                  {formatDays(r.median_days)}
                </td>
                <td className="py-1.5 text-right text-slate-500 tabular-nums">{formatNum(r.delivered)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ── Area (Metro / Rural) performance, per state ──────────────────────────────
//
// Median transit split by AU state × Australia Post metro/rural postcode
// classification (eParcel metro bound). One row per state with a Metro
// column and a Rural column, so you can see e.g. NSW-metro (Sydney) vs
// NSW-rural. Postcode-based; international / malformed-postcode orders are
// excluded. Cells with < 3 delivered render as a dash.

function AreaStatePerformanceTable({ title, subtitle, rows }) {
  const empty = !rows || rows.length === 0
  return (
    <div className="card p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {empty ? (
        <p className="text-xs text-slate-400 text-center py-6">
          Not enough delivered shipments in this class yet (need ≥ 3 per cell).
        </p>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr>
              <th className="text-left font-medium py-1">State</th>
              <th className="text-right font-medium py-1">Metro</th>
              <th className="text-right font-medium py-1">Rural</th>
              <th className="text-right font-medium py-1">Delivered</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.state} className="border-t border-slate-100">
                <td className="py-1.5 font-medium text-slate-700">{r.state}</td>
                <td className="py-1.5 text-right tabular-nums text-slate-700">{formatDays(r.metro_median)}</td>
                <td className="py-1.5 text-right tabular-nums text-slate-700">{formatDays(r.rural_median)}</td>
                <td className="py-1.5 text-right tabular-nums text-slate-500">{formatNum((r.metro_delivered || 0) + (r.rural_delivered || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ── State × weight matrix ────────────────────────────────────────────────────
//
// Median transit as a heatmap: state rows, 500g weight-bucket columns.
// Cells are colour-graded on an absolute AU-domestic scale (≤2d fast →
// >5d slow) so a glance shows where weight or distance drags delivery.
// Cells with < 3 delivered come through null and render as a dash.

function weightCellClass(days) {
  if (days == null) return 'text-slate-300'
  if (days <= 2)  return 'text-emerald-700 bg-emerald-50'
  if (days <= 3)  return 'text-emerald-800'
  if (days <= 5)  return 'text-amber-700'
  return 'text-red-700 bg-red-50'
}

function WeightStateMatrix({ title, subtitle, matrix }) {
  const buckets = matrix?.buckets || []
  const rows = matrix?.rows || []
  return (
    <div className="card p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-6">
          Not enough delivered shipments to build the matrix yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left font-medium py-1 pr-2 sticky left-0 bg-white">State</th>
                {buckets.map(b => (
                  <th key={b} className="text-right font-medium py-1 px-1.5 whitespace-nowrap">{b}</th>
                ))}
                <th className="text-right font-medium py-1 pl-2">Delivered</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.state} className="border-t border-slate-100">
                  <td className="py-1.5 pr-2 font-medium text-slate-700 sticky left-0 bg-white">{r.state}</td>
                  {r.cells.map((c, i) => (
                    <td key={i} className={`py-1.5 px-1.5 text-right tabular-nums rounded ${weightCellClass(c.median_days)}`}>
                      {c.median_days != null ? `${c.median_days.toFixed(1)}d` : '—'}
                    </td>
                  ))}
                  <td className="py-1.5 pl-2 text-right text-slate-500 tabular-nums">{formatNum(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Main tab ────────────────────────────────────────────────────────────────

export default function TrackingOverviewTab() {
  const [days, setDays] = useState(30)
  // Client filter lives in the URL as ?client_id=. The dropdown that
  // drives it is in the Tracking page shell (above the tabs); this tab
  // just reads the param.
  const [params] = useSearchParams()
  const clientId = params.get('client_id') || undefined

  const { data, isLoading } = useQuery({
    queryKey: ['tracking', 'overview', days, clientId],
    queryFn: () => getTrackingOverview({ days, client_id: clientId }),
    refetchInterval: 60_000,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 gap-2 text-slate-400">
        <Loader2 size={16} className="animate-spin" /> Loading overview…
      </div>
    )
  }

  const k  = data?.kpis || {}
  const sd = data?.status_distribution || []
  const dv = data?.daily_volume || []
  const sp = data?.state_performance || { standard: [], express: [] }
  const ws = data?.weight_state || { standard: null, express: null }
  const ap = data?.area_state_performance || { standard: [], express: [] }

  return (
    <div className="space-y-5">
      {/* Window selector */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-slate-500">
          Window: {data?.window?.from} → {data?.window?.to}
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden bg-white text-xs">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={[
                'px-3 py-1.5 font-medium transition-colors',
                d === days ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50',
              ].join(' ')}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* KPI grid. Median + P90 use SplitKpiTile so Std and Express
          show as separate halves — a single combined number hides the
          Express premium operators want to see. */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiTile icon={Plane}          label="In-flight"     value={formatNum(k.in_flight)} />
        <KpiTile icon={PackageCheck}   label="Delivered"     value={formatNum(k.delivered_in_window)} sub={`in last ${days}d`} accent="text-emerald-600" />
        <KpiTile icon={AlertTriangle}  label="Exceptions"    value={formatNum(k.exception_in_window)} sub={`in last ${days}d`} accent={k.exception_in_window > 0 ? 'text-red-600' : 'text-slate-700'} />
        <SplitKpiTile
          icon={Timer}          label="Median transit"
          leftValue={formatDays(k.median_transit_days_std)}  leftCount={k.delivered_std_count}
          rightValue={formatDays(k.median_transit_days_exp)} rightCount={k.delivered_exp_count}
        />
        <SplitKpiTile
          icon={BarChart2}      label="P90 transit"
          leftValue={formatDays(k.p90_transit_days_std)}     leftCount={k.delivered_std_count}
          rightValue={formatDays(k.p90_transit_days_exp)}    rightCount={k.delivered_exp_count}
        />
        <KpiTile icon={Radar}          label="Coverage"      value={k.coverage_pct != null ? `${k.coverage_pct}%` : '—'} sub="AP orders with events" />
      </div>

      {/* Status + volume row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <StatusDonut rows={sd} />
        <div className="lg:col-span-2"><DailyVolumeChart rows={dv} /></div>
      </div>

      {/* State-by-state performance, split by service class.
          Each table is independently sorted (fastest at top). Side-by-
          side comparison makes the Express premium visible per state —
          e.g. WA Std 6.27d vs WA Exp 2.23d. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <StatePerformanceTable
          title="Standard Post — by state"
          subtitle="Median transit time. Fastest at top."
          rows={sp.standard || []}
        />
        <StatePerformanceTable
          title="Express Post — by state"
          subtitle="Median transit time. Fastest at top."
          rows={sp.express || []}
        />
      </div>

      {/* Area (Metro / Rural) breakdown per state — AusPost eParcel
          postcode classification. Metro = the capital's parcel-metro
          bound; everything else in the state is Rural. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <AreaStatePerformanceTable
          title="Standard Post — Metro vs Rural by state"
          subtitle="Median transit time. AusPost eParcel metro/rural postcode split."
          rows={ap.standard || []}
        />
        <AreaStatePerformanceTable
          title="Express Post — Metro vs Rural by state"
          subtitle="Median transit time. AusPost eParcel metro/rural postcode split."
          rows={ap.express || []}
        />
      </div>

      {/* State × weight matrices, split by service class. Rows = state,
          columns = 500g weight buckets, cells = median transit. */}
      <div className="grid grid-cols-1 gap-3">
        <WeightStateMatrix
          title="Standard Post — transit by state × weight"
          subtitle="Median transit time (days). Cells with fewer than 3 deliveries show a dash."
          matrix={ws.standard}
        />
        <WeightStateMatrix
          title="Express Post — transit by state × weight"
          subtitle="Median transit time (days). Cells with fewer than 3 deliveries show a dash."
          matrix={ws.express}
        />
      </div>
    </div>
  )
}
